import React, { useState, useCallback } from "react";
import {
  ArrowRight,
  Combine,
  HelpCircle,
  RefreshCcw,
  Search,
} from "lucide-react";
import ProductListing from "../salesInvoice/ProductsModal";
import CreditCustomerList from "../common/CreditCustomerList";
import Modal from "../common/CommonModal";
import useApi from "../../hooks/useApi";

const ActionCard = ({
  title,
  sections,
  onKeyDown,
  formData,
  setFormData,
  onAction,
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex-1 flex flex-col">
    <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50">
      <h2 className="text-sm font-bold text-slate-700 uppercase tracking-tight flex items-center gap-2">
        {title}
      </h2>
    </div>
    <div className="p-5 flex-1 flex flex-col justify-between">
      <div className="space-y-12">
        {sections.map((section, idx) => (
          <div key={idx} className="space-y-4">
            <div className="flex flex-col space-y-3">
              <div className="flex items-center gap-3">
                {/* Input 1 (Always Source - usually F2 only) */}
                <div className="flex-1 space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter flex items-center justify-between">
                    {section.label1}
                    <span className="text-[9px] text-orange-500 font-bold bg-orange-50 px-1 rounded border border-orange-100 uppercase">
                      F2 ONLY
                    </span>
                  </label>
                  <div className="relative group">
                    <input
                      type="text"
                      readOnly
                      placeholder="Click or F2..."
                      value={formData[section.key1] || ""}
                      onClick={() =>
                        onKeyDown(
                          { key: "F2", preventDefault: () => {} },
                          section.key1,
                          section.searchType,
                        )
                      }
                      onKeyDown={(e) =>
                        onKeyDown(e, section.key1, section.searchType)
                      }
                      className="w-full pl-3 pr-8 py-2 text-sm bg-slate-50 border border-slate-200 rounded cursor-pointer hover:border-blue-300 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    />
                    <Search className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                  </div>
                </div>

                <div className="pt-5 text-slate-300">
                  <ArrowRight size={14} />
                </div>

                {/* Input 2 (Destination - Dynamic Badge) */}
                <div className="flex-1 space-y-1">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter flex items-center justify-between">
                    <span className="flex items-center gap-1">
                      {section.label2}
                      <div className="group/info relative cursor-help">
                        <HelpCircle
                          size={12}
                          className="text-slate-300 hover:text-blue-500 transition-colors"
                        />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/info:block w-48 p-2 bg-slate-800 text-white text-[10px] rounded shadow-lg z-50 leading-tight normal-case font-normal">
                          {section.info}
                          <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-slate-800"></div>
                        </div>
                      </div>
                    </span>

                    {/* DYNAMIC BADGE LOGIC HERE */}
                    {section.canType ? (
                      <span className="text-[9px] text-blue-500 font-bold bg-blue-50 px-1 rounded border border-blue-100 uppercase">
                        Typeable
                      </span>
                    ) : (
                      <span className="text-[9px] text-orange-500 font-bold bg-orange-50 px-1 rounded border border-orange-100 uppercase">
                        F2 ONLY
                      </span>
                    )}
                  </label>

                  <div className="relative group">
                    <input
                      type="text"
                      readOnly={!section.canType}
                      placeholder={
                        section.canType ? "Enter value..." : "Click or F2..."
                      }
                      value={formData[section.key2] || ""}
                      onChange={(e) =>
                        section.canType &&
                        setFormData((p) => ({
                          ...p,
                          [section.key2]: e.target.value,
                        }))
                      }
                      onClick={() =>
                        !section.canType &&
                        onKeyDown(
                          { key: "F2", preventDefault: () => {} },
                          section.key2,
                          section.searchType,
                        )
                      }
                      onKeyDown={(e) =>
                        onKeyDown(e, section.key2, section.searchType)
                      }
                      className={`w-full pl-3 pr-8 py-2 text-sm border rounded outline-none transition-all ${
                        section.canType
                          ? "bg-white border-slate-300 focus:ring-2 focus:ring-blue-500"
                          : "bg-slate-50 border-slate-200 cursor-pointer hover:border-blue-300 focus:ring-2 focus:ring-blue-500"
                      }`}
                    />
                    {!section.canType && (
                      <Search className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={() => onAction(section)}
                className={`w-full py-2.5 rounded text-xs font-bold uppercase tracking-wide flex items-center justify-center gap-2 transition-all shadow-sm ${
                  section.isMerge
                    ? "bg-green-600 text-white hover:bg-green-700 active:scale-[0.98]"
                    : "bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]"
                }`}
              >
                {section.isMerge ? (
                  <Combine size={14} />
                ) : (
                  <RefreshCcw size={14} />
                )}
                {section.buttonText}
              </button>

              <p className="text-[10px] text-slate-400 italic px-1 leading-relaxed">
                {section.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
export default function ManageMasters() {
  const [formData, setFormData] = useState({});
  const [activeRefField, setActiveRefField] = useState(null);
  const [showItemSearch, setShowItemSearch] = useState(false);
  const [showAccountSearch, setShowAccountSearch] = useState(false);

  // Stock Search state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchBy, setSearchBy] = useState("UNIVERSAL");
  const { request } = useApi();

  const COMPANY_CODE = localStorage?.getItem("companyCode") || "DOODDEMO";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";

  // Fetch product data to pass to ProductListing
  const fetchProductData = useCallback(
    async (query, searchByValue) => {
      const effectiveQuery = query?.trim() === "" ? "-" : query?.trim() || "-";
      const formattedQuery = effectiveQuery.replace(/\//g, "%2F");

      try {
        setLoading(true);
        const url = `${BASE_URL}/api/CRMStockList/${COMPANY_CODE}/${searchByValue}/${formattedQuery}/A/-`;
        const response = await request({ url, method: "GET" });
        setProducts(response || []);
      } catch (error) {
        console.error("Error while fetching Stock list!", error);
      } finally {
        setLoading(false);
      }
    },
    [BASE_URL, COMPANY_CODE, request],
  );

  const handleAction = async (section) => {
    const code1 = formData[section.key1];
    const code2 = formData[section.key2];

    if (!code1 || !code2) {
      alert("Please enter both source and target codes.");
      return;
    }
    if (code1 === code2) {
      alert("Source and target codes cannot be the same.");
      return;
    }

    const payload = [
      {
        CmpCode: COMPANY_CODE,
        mode: section.apiMode,
        code1: code1,
        code2: code2,
        user: localStorage?.getItem("currentUserName") || "admin",
        deptno: localStorage.getItem("currentSelectedDepartment") || "MAIN",
      },
    ];

    try {
      setLoading(true);
      const url = `${BASE_URL}/api/CRMChangeMerge`;
      // Use the request helper if it supports POST with data, otherwise use fetch/axios
      const response = await request({
        url,
        method: "POST",
        data: payload,
      });

      console.log("Action response:", response);
      alert(`${section.buttonText} completed successfully.`);

      // Clear inputs for this section
      setFormData((prev) => ({
        ...prev,
        [section.key1]: "",
        [section.key2]: "",
      }));
    } catch (error) {
      console.error("Action error:", error);
      alert(
        `Failed to complete ${section.buttonText}. Check console for details.`,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e, fieldKey, searchType) => {
    if (e.key === "F2") {
      e.preventDefault();
      setActiveRefField(fieldKey);
      if (searchType === "item") {
        setShowItemSearch(true);
        fetchProductData("-", "UNIVERSAL");
      } else if (searchType === "account") {
        setShowAccountSearch(true);
      }
    }
  };

  const handleProductSelect = (product) => {
    if (activeRefField && product?.Code) {
      setFormData((prev) => ({ ...prev, [activeRefField]: product.Code }));
    }
    setShowItemSearch(false);
  };

  const handleAccountSelect = (account) => {
    if (activeRefField && account?.ACCOUNT) {
      setFormData((prev) => ({ ...prev, [activeRefField]: account.ACCOUNT }));
    }
    setShowAccountSearch(false);
  };

  const itemSections = [
    {
      label1: "Existing Code",
      label2: "New Code",
      buttonText: "Change Item Code",
      isMerge: false,
      canType: true,
      key1: "itemChange_existing",
      key2: "itemChange_new",
      searchType: "item",
      apiMode: "ITEMCHANGE",
      info: "Used to rename a product code without losing history.",
      description:
        "Note: This will update the code across all historical invoices.",
    },
    {
      label1: "Source Item",
      label2: "Dest. Item",
      buttonText: "Merge Item Code",
      isMerge: true,
      canType: false,
      key1: "itemMerge_source",
      key2: "itemMerge_dest",
      searchType: "item",
      apiMode: "ITEMMERGE",
      info: "Merges all stock and history from Source into Destination.",
      description:
        "Warning: Source item will be deleted after merging into Destination.",
    },
  ];

  const accountSections = [
    {
      label1: "Existing Account",
      label2: "New Account",
      buttonText: "Change Account No",
      isMerge: false,
      canType: true, // USER CAN TYPE HERE
      key1: "accountChange_existing",
      key2: "accountChange_new",
      searchType: "account",
      apiMode: "ACCOUNTCHANGE",
      info: "Used to rename an account number with new account number",
      description:
        "Note: This will update the account number across all historical invoices.",
    },
    {
      label1: "Source Account",
      label2: "Dest. Account",
      buttonText: "Merge Account No",
      isMerge: true,
      canType: false, // MUST USE F2
      key1: "accountMerge_source",
      key2: "accountMerge_dest",
      searchType: "account",
      apiMode: "ACCOUNTMERGE",
      info: "Merges all stock and history from Source into Destination.",
      description:
        "Warning: Source account will be deleted after merging into Destination.",
    },
  ];

  return (
    <div className="p-3 ">
      <div className=" mx-auto space-y-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <ActionCard
            title="Item Management"
            sections={itemSections}
            onKeyDown={handleKeyDown}
            formData={formData}
            setFormData={setFormData}
            onAction={handleAction}
          />
          <ActionCard
            title="Account Management"
            sections={accountSections}
            onKeyDown={handleKeyDown}
            formData={formData}
            setFormData={setFormData}
            onAction={handleAction}
          />
        </div>
      </div>

      {/* Item Search Modal */}
      <Modal
        isOpen={showItemSearch}
        onClose={() => setShowItemSearch(false)}
        title="Search Products"
        size="xxl"
      >
        <ProductListing
          modalSuggestions={products}
          loading={loading}
          searchQuery={searchQuery}
          searchBy={searchBy}
          setSearchBy={setSearchBy}
          onSearchQueryChange={(query) => {
            setSearchQuery(query);
            fetchProductData(query, searchBy);
          }}
          handleProductSelect={handleProductSelect}
          pageNumber={1}
          pageSize={100}
          totalRows={products.length}
          onPageChange={() => {}}
        />
      </Modal>

      {/* Account Search Modal */}
      <Modal
        isOpen={showAccountSearch}
        onClose={() => setShowAccountSearch(false)}
        title="Search Accounts"
        size="xxl"
      >
        <CreditCustomerList
          handleCustomerSelect={handleAccountSelect}
          showSidebar={true}
        />
      </Modal>
    </div>
  );
}
